/**
 * A start time, as minutes from local midnight.
 *
 * `<input type="time">` rather than a text box, because a text box is close to
 * unusable on a phone: `inputMode="numeric"` gives iOS a digits-only keypad
 * with no colon, so "08:30" cannot be typed, and the value only committed on
 * blur — which on iOS is the same tap that was meant to hit Save. The native
 * control is a wheel picker there and a segmented field on desktop, and it
 * commits on every change.
 *
 * The value stays wall-clock throughout (ADR-002): the element's own value is
 * always a 24-hour `HH:MM` string regardless of what the device displays, so
 * nothing here goes near a Date.
 */
import { Input } from "@/components/ui/input";
import { formatMinute, parseMinute } from "@/lib/dates";
import { cn } from "@/lib/utils";

interface TimeFieldProps {
  id?: string;
  /** Minutes from local midnight, 0–1439. */
  value: number;
  onChange: (minute: number) => void;
  className?: string;
}

export function TimeField({ id, value, onChange, className }: TimeFieldProps) {
  return (
    <Input
      id={id}
      type="time"
      value={formatMinute(value)}
      onChange={(event) => {
        const parsed = parseMinute(event.target.value);
        // Clearing the field, and the half-typed states some desktop browsers
        // emit mid-edit, both arrive as "". Keep the last good time rather
        // than snapping the block to midnight under the user.
        if (parsed !== null) onChange(parsed);
      }}
      className={cn("font-mono tabular-nums", className)}
    />
  );
}
